import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import LoginPage from "./pages/Login";
import RegisterPage from "./pages/Register";
import ResetPasswordPage from "./pages/ResetPassword";
import MfaVerifyPage from "./pages/MfaVerify";
import AuthCallback from "./pages/AuthCallback";
import AppLayout from "./components/AppLayout";
import DashboardPage from "./pages/Dashboard";
import FinancePage from "./pages/Finance";
import HRPage from "./pages/HR";
import HrSelfServicePage from "./pages/HrSelfService";
import SCMPage from "./pages/SCM";
import HSEPage from "./pages/HSE";
import EAMPage from "./pages/EAM";
import AnalyticsPage from "./pages/Analytics";
import OrgPage from "./pages/Org";
import SettingsPage from "./pages/Settings";
import AuditLogPage from "./pages/AuditLog";
import CashHealthCheckPage from "./pages/CashHealthCheck";
import SharedHealthCheck from "./pages/SharedHealthCheck";
import Leaderboard from "./pages/Leaderboard";
import AdminLeadsPage from "./pages/AdminLeads";
import Workflows from "./pages/Workflows";
import Approvals from "./pages/Approvals";
import Affiliates from "./pages/Affiliates";
import OmanCompliancePage from "./pages/Oman";
import IFRSPage from "./pages/IFRS";
import BankingPage from "./pages/Banking";
import AMLPage from "./pages/AML";
import PlatformPage from "./pages/Platform";
import ConsolidationPage from "./pages/Consolidation";
import BillingSeats from "./pages/BillingSeats";
import BillingReturn from "./pages/BillingReturn";
import ActivatePage from "./pages/Activate";

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="font-mono text-xs text-zinc-500 pulse-dot" data-testid="loading-indicator">LOADING SESSION…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user && user !== null) {
    const dest = user.role === "superuser" ? "/platform" : "/";
    return <Navigate to={dest} replace />;
  }
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // OAuth callback handling - check hash synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/activate" element={<ActivatePage />} />
      <Route path="/mfa" element={<PublicOnly><MfaVerifyPage /></PublicOnly>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/cash-health-check" element={<CashHealthCheckPage />} />
      <Route path="/cash-health-check/r/:shareId" element={<SharedHealthCheck />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/platform" element={<ProtectedRoute><PlatformPage /></ProtectedRoute>} />
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/finance" element={<FinancePage />} />
        <Route path="/oman" element={<OmanCompliancePage />} />
        <Route path="/ifrs" element={<IFRSPage />} />
        <Route path="/banking" element={<BankingPage />} />
        <Route path="/aml" element={<AMLPage />} />
        <Route path="/consolidation" element={<ConsolidationPage />} />
        <Route path="/hr" element={<HRPage />} />
        <Route path="/hr/self-service" element={<HrSelfServicePage />} />
        <Route path="/scm" element={<SCMPage />} />
        <Route path="/hse" element={<HSEPage />} />
        <Route path="/eam" element={<EAMPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/org" element={<OrgPage />} />
        <Route path="/billing/seats" element={<BillingSeats />} />
        <Route path="/billing/return" element={<BillingReturn />} />
        <Route path="/admin/leads" element={<AdminLeadsPage />} />
        <Route path="/admin/affiliates" element={<Affiliates />} />
        <Route path="/workflows" element={<Workflows />} />
        <Route path="/approvals" element={<Approvals />} />
        <Route path="/audit" element={<AuditLogPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const TOASTER_OPTIONS = {
  style: {
    borderRadius: 0,
    border: "1px solid #09090b",
    fontFamily: "IBM Plex Sans, sans-serif",
  },
};

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="bottom-right" toastOptions={TOASTER_OPTIONS} />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
